import React, { forwardRef } from "react";
import DiamondShapeSVG from "../DiamondShapeSVG";

const fmtVal = (v) =>
  typeof v === "boolean" ? (v ? "Yes" : "No") : v === null || v === undefined ? "—" : String(v);

const MinimalZen = forwardRef(({
  product,
  buyer,
  cardScale = 100,
  productImageRef,
  visibleFields = {},
  cssVars = {},
  forExport = false,
}, ref) => {
  const isVisible = (key) => visibleFields[key] !== false;

  const price = product?.pricing?.retail || product?.attributes?.retail_price || 0;
  const formatPrice = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);

  const segmentAttrEntries = Object.entries(product?.attributes || {})
    .filter(([k]) => k !== "retail_price")
    .filter(([k]) => isVisible(k));

  const rootStyle = {
    transform: forExport ? "scale(1)" : `scale(${cardScale / 100})`,
    transformOrigin: "center top",
    width: "420px",
    height: "560px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...cssVars,
  };

  return (
    <div ref={ref} style={rootStyle} className="card-preview-wrapper layout-minimal-zen">

      {/* ── Background ── */}
      <div style={{ position: "absolute", inset: 0, background: "var(--theme-bg)", zIndex: 0 }} />

      {/* ── Zen circle decoration (Enso-inspired) ── */}
      <svg style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }}
        width="420" height="560" viewBox="0 0 420 560">
        {/* Large off-center circle */}
        <circle cx="320" cy="130" r="155"
          fill="none" stroke="var(--theme-accent)"
          strokeWidth="0.8" opacity="0.1" />
        <circle cx="320" cy="130" r="135"
          fill="none" stroke="var(--theme-accent)"
          strokeWidth="0.4" opacity="0.07" />
        {/* Bottom left small circle */}
        <circle cx="60" cy="480" r="80"
          fill="none" stroke="var(--theme-accent)"
          strokeWidth="0.6" opacity="0.09" />
        {/* Thin vertical accent line */}
        <line x1="40" y1="40" x2="40" y2="520"
          stroke="var(--theme-accent)" strokeWidth="1" opacity="0.2" />
        {/* Horizontal hairline */}
        <line x1="60" y1="290" x2="390" y2="290"
          stroke="var(--theme-accent)" strokeWidth="0.5" opacity="0.15" />
      </svg>

      {/* ── Thin left accent line ── */}
      <div style={{
        position: "absolute", top: 32, left: 18, bottom: 32, width: 1.5,
        background: "var(--theme-accent)", opacity: 0.55, zIndex: 4,
      }} />

      {/* ── Top accent line ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: "var(--theme-accent)", opacity: 0.6, zIndex: 10,
      }} />

      {/* ── Content ── */}
      <div style={{
        position: "relative", zIndex: 5,
        display: "flex", flexDirection: "column",
        height: "100%", padding: "20px 26px 18px 36px",
      }}>

        {/* ── Segment chip (top, ultra minimal) ── */}
        <div style={{ marginBottom: 14 }}>
          <span style={{
            fontSize: "0.5rem", fontWeight: 700, letterSpacing: "0.35em",
            color: "var(--theme-accent)", textTransform: "uppercase",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          }}>
            {product?.segment?.toUpperCase() || "JEWELLERY"}
          </span>
        </div>

        {/* ── Brand name ── */}
        {isVisible("organizationName") && (
          <div style={{
            fontFamily: "var(--theme-heading-font,'Cormorant Garamond','Georgia',serif)",
            fontSize: "1.6rem", fontWeight: 300, letterSpacing: "0.08em",
            color: "var(--theme-text)", lineHeight: 1, marginBottom: 6,
          }}>
            {buyer?.organizationName || "Your Brand"}
          </div>
        )}

        {/* ── Hairline ── */}
        <div style={{
          width: 40, height: 1, background: "var(--theme-accent)",
          marginBottom: 18, opacity: 0.7,
        }} />

        {/* ── Product image (clean, centered) ── */}
        <div style={{
          flex: "0 0 185px",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 16,
        }}>
          <img
            src={Array.isArray(product?.image_url) ? product.image_url[0] : product?.image_url}
            alt={product?.name || ""}
            crossOrigin="anonymous"
            style={{
              maxWidth: "68%", maxHeight: "100%", objectFit: "contain",
              filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.22))",
            }}
          />
        </div>

        {/* ── Product name ── */}
        <div style={{ marginBottom: 10 }}>
          <h2 style={{
            fontFamily: "var(--theme-heading-font,'Cormorant Garamond','Georgia',serif)",
            fontSize: "1.25rem", fontWeight: 400,
            color: "var(--theme-text)", margin: "0 0 3px",
            letterSpacing: "0.04em", lineHeight: 1.2,
          }}>
            {product?.name || "Fine Jewellery"}
          </h2>
          {product?.description && (
            <p style={{
              fontSize: "0.6rem", color: "var(--theme-textMuted)",
              fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
              lineHeight: 1.5, margin: 0,
            }}>
              {product.description.slice(0, 72)}{product.description.length > 72 ? "…" : ""}
            </p>
          )}
        </div>

        {/* ── Attributes (minimal list style) ── */}
        {segmentAttrEntries.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {segmentAttrEntries.map(([k, v]) => (
              <div key={k} style={{
                display: "flex", alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid var(--theme-border)",
                padding: "3px 0",
              }}>
                <span style={{
                  fontSize: "0.52rem", fontWeight: 600,
                  color: "var(--theme-textMuted)",
                  textTransform: "uppercase", letterSpacing: "0.1em",
                  fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                }}>
                  {k.replace(/_/g, " ")}
                </span>
                {k === "shape" && product?.segment?.toLowerCase() === "diamond" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <DiamondShapeSVG shapeName={String(v)} size={18} color="var(--theme-accent)" />
                    <span style={{
                      fontSize: "0.62rem", fontWeight: 600, color: "var(--theme-text)",
                      fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                    }}>{fmtVal(v)}</span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: "0.62rem", fontWeight: 600, color: "var(--theme-text)",
                    fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                  }}>{fmtVal(v)}</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* ── Price ── */}
        <div style={{
          display: "flex", alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 10,
        }}>
          <div>
            <div style={{
              fontSize: "0.45rem", letterSpacing: "0.2em",
              color: "var(--theme-textMuted)", textTransform: "uppercase",
              fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
              marginBottom: 1,
            }}>
              Price
            </div>
            <div style={{
              fontFamily: "var(--theme-heading-font,'Cormorant Garamond',serif)",
              fontSize: "1.5rem", fontWeight: 600,
              color: "var(--theme-text)", lineHeight: 1,
            }}>
              {formatPrice(price)}
            </div>
          </div>
          {/* simple arrow CTA */}
          <div style={{
            display: "flex", alignItems: "center", gap: 5,
            fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.12em",
            color: "var(--theme-accent)",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            textTransform: "uppercase",
          }}>
            <span>Enquire</span>
            <svg width="18" height="10" viewBox="0 0 18 10">
              <path d="M0,5 H15 M11,1 L15,5 L11,9"
                stroke="var(--theme-accent)" strokeWidth="1.5"
                fill="none" strokeLinecap="round" />
            </svg>
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderTop: "1px solid var(--theme-border)", paddingTop: 5,
          fontSize: "0.48rem", color: "var(--theme-textMuted)",
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          letterSpacing: "0.1em",
        }}>
          {isVisible("address") && <span>{buyer?.streetAddress || "123 Jewellers Lane"}</span>}
          {isVisible("phone") && <span>{buyer?.transactionalMobile || buyer?.mobile || "+91 98765 43210"}</span>}
        </div>
      </div>
    </div>
  );
});

MinimalZen.displayName = "MinimalZen";
export default MinimalZen;
